import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useChangePreferredLanguage } from '@/entities/user';
import {
  useAppSettingsStore,
  useLanguageOptions,
  useToast,
} from '@/shared/lib';
import type { Language } from '@/shared/types';
import LanguageIcon from '@/shared/assets/language-icon.svg?react';
import { OptionListRow, type OptionListItem } from './OptionListRow';

type Props = {
  isTabletUp: boolean;
};

export function LanguageRow({ isTabletUp }: Props) {
  const { t } = useTranslation();
  const { getLanguage } = useAppSettingsStore();
  const { changeLanguage, isPending } = useChangePreferredLanguage();
  const { toast } = useToast();
  const { languageOptions } = useLanguageOptions();
  const language = getLanguage();

  const items = useMemo<OptionListItem[]>(
    () =>
      languageOptions.map(option => ({
        key: option.value,
        label: t(`common.language.options.${option.value}`),
        icon: option.icon,
      })),
    [languageOptions, t]
  );

  const handleSelect = async (key: string) => {
    try {
      await changeLanguage(key as Language);
    } catch (error) {
      toast.error(
        (error instanceof Error && error.message) || t('common.unknownError')
      );
    }
  };

  return (
    <OptionListRow
      icon={<LanguageIcon />}
      label={t('common.language.title')}
      valueLabel={t(`common.language.options.${language}`)}
      items={items}
      onSelect={handleSelect}
      isTabletUp={isTabletUp}
      disabled={isPending}
    />
  );
}
